import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Box, Grid, Stack, Typography } from '@mui/material'

import { useDiscoverNetworksQuery } from '@/core/services/network'

import NetworkCard from '@/components/ui/cards/NetworkCard'

import { isConsultationOrder, isExpertiseOrder } from '@/utils/type-guards'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import TextInput from '@/components/ui/inputs/TextInput'
import { useDebouncedCallback } from 'use-debounce'
import { useState } from 'react'

export default function DiscoverNetworks() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const { networks } = useDiscoverNetworksQuery(
    { skip: 0 },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data: networks }) => ({
        networks: networks?.map((network) => ({
          id: network._id,
          coverPhoto: network.coverPhoto?.url,
          memberCount: network.members.length,
          name: network.name,
          pendingMembers: _.map(network.pendingMembers, '_id'),
          pendingOrders: [
            {
              count: _.filter(network.pendingOrders, isConsultationOrder).length,
              type: 'consultation' as const,
            },
            {
              count: _.filter(network.pendingOrders, isExpertiseOrder).length,
              type: 'expertise' as const,
            },
          ],
          privacy: network.privacy,
        })),
      }),
    }
  )
  const [searchQuery, setSearchQuery] = useState('')

  const debouncedOnChange = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase())
  }, 300)
  
  const filteredNetworks = networks?.filter(network => 
    network.name.toLowerCase().includes(searchQuery)
  )

  return (
    <Stack px={isMobile ? 4 : 8} spacing={3} width="100%">
      <Stack direction="row" pt={isMobile ? 2 : 4}>
        <Typography component="h2" sx={{ fontSize: 30, fontWeight: 700 }}>
          {t('page.networks.discover.title')}
        </Typography>
      </Stack>
      {isMobile &&
        <Grid item xs={12} lg={8} xl={9}>
          <TextInput fullWidth onChange={debouncedOnChange} placeholder={t('page.networks.searchInput.placeholder')} />
        </Grid>}
      <Box>
        <Grid container spacing={2}>
          {_.map(filteredNetworks, (network) => (
            <Grid key={network.id} item xs={12} lg={6} xl={4}>
              <NetworkCard
                network={network}
                onSelect={() => {
                  navigate(`/networks/${network.id}`)
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  )
}
